import { isCommercialList } from '@epicuro-next/features/engagement/list-utils';
import { List, ProductReference } from '@epicuro-next/models/engagement';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { Product } from 'graphql-api/gql-api-interfaces';

import * as actions from '../../actions';
import {
  calculateTotalProductsOnDelete,
  mapAvailableUnavailableTotalProducts,
} from '../../utils';
/**
 * Product List feature key
 */
export const productListOpenedFeatureKey = 'opened';
/**
 * ProductList State
 */
export interface State {
  /**
   * Id of the active list
   */
  list: List | null;

  /** Products list */
  products: EntityState<Product>;

  epicShowProducts: EntityState<Product>;

  epicShowProductReferences: EntityState<ProductReference>;

  /** Products total count(on on server. Equals to totalAvailable or totalUnavailable in ideal scenarios */
  total: number | null;

  /** Available products total count(on a server) */
  totalAvailable?: number;

  /**
   * the result page to fetch
   */
  page: number;

  /**
   * Page size
   */
  size: number;

  /** loading indicator */
  loading: boolean;

  /** loading state for shop the list button */
  shopTheListLoading: boolean;

  loadListInDemand: boolean;
}

function selectProductId(product: Product): string {
  return product.productId;
}

/** State adapter */
export const adapter: EntityAdapter<Product> = createEntityAdapter<Product>({
  selectId: selectProductId,
});

export const epicShowProductsAdapter: EntityAdapter<Product> =
  createEntityAdapter<Product>({
    selectId: selectProductId,
  });

export const epicShowProductReferencesAdapter: EntityAdapter<ProductReference> =
  createEntityAdapter<Product>({
    selectId: selectProductId,
  });

/**
 * Initial state for ProductList
 */
export const initialState: State = {
  list: null,
  products: adapter.getInitialState(),
  epicShowProducts: epicShowProductsAdapter.getInitialState(),
  page: 0,
  size: 10,
  loading: false,
  shopTheListLoading: false,
  total: null,
  loadListInDemand: true,
  epicShowProductReferences: epicShowProductReferencesAdapter.getInitialState(),
};

/**
 * ProductList Reducer
 */
const productListOpenedReducer = createReducer(
  initialState,
  on(
    actions.updateProductListSuccess,
    actions.updateCommercialListSuccess,
    (state, { list, skipRedirectionToList }): State => {
      const isAutoSaveAfterOpenedListSwitched =
        skipRedirectionToList && list?.id !== state.list?.id;
      if (isAutoSaveAfterOpenedListSwitched) {
        return state;
      }

      return {
        ...state,
        list,
        total: list.amount ?? 0,
        totalAvailable: list.availableProductsCount ?? 0,
      };
    },
  ),

  on(actions.exportListToEmailSuccess, (state, { emailData }): State => {
    const newRecipients = (state.list?.recipients?.emails ?? []).concat(
      (emailData?.recipients ?? []).reduce(
        (acc, { email }) => [...acc, email],
        [],
      ),
    );

    return {
      ...state,
      list: {
        ...state.list,
        recipients: {
          count: newRecipients?.length,
          emails: newRecipients,
        },
      } as List,
    };
  }),
  on(actions.setOpenedList, (state, action): State => {
    const list = action.id !== state.list?.id ? null : state.list;
    return {
      ...state,
      list,
    };
  }),
  on(actions.setOpenedListSuccess, (_, action): State => {
    const { list } = action;
    return {
      ...initialState,
      list,
      total: list.amount ?? 0,
      totalAvailable: list.availableProductsCount ?? 0,
    };
  }),
  on(actions.setOpenedListMedia, (state, { media }): State => {
    return {
      ...state,
      list: {
        ...state.list,
        mainMedia: media,
      } as List,
    };
  }),
  on(actions.loadOpenedListProducts, (state): State => {
    if (state.list && isCommercialList(state.list)) return state;

    return {
      ...state,
      loading: state.loadListInDemand,
    };
  }),
  on(
    actions.shopTheList,
    (state): State => ({
      ...state,
      shopTheListLoading: true,
    }),
  ),
  on(
    actions.setFullList,
    actions.shopTheListError,
    (state): State => ({
      ...state,
      shopTheListLoading: false,
    }),
  ),
  on(
    actions.loadOpenedListProductsSuccess,
    (state, { products, samePage, total, addToTop }): State => {
      if (state.list && isCommercialList(state.list)) return state;

      const newProducts = adapter.upsertMany(products, state.products);

      let sortedIdsForNewProducts: string[] = [];

      if (addToTop) {
        sortedIdsForNewProducts = addProductsToTop(
          newProducts,
          products,
          state.products.ids as string[],
        );
      }

      return {
        ...state,
        products: {
          ids: addToTop ? sortedIdsForNewProducts : newProducts.ids,
          entities: newProducts.entities,
        },
        page: !samePage ? state.page + 1 : state.page,
        loading: false,
        loadListInDemand: false,
        total: total ?? (state.total ?? 0) + (samePage ? products.length : 0),
      };
    },
  ),
  on(
    actions.updateOpenedListProductsWithDiscounts,
    (state, { products }): State => {
      if (state.list && isCommercialList(state.list)) return state;

      return {
        ...state,
        products: adapter.updateMany(
          products.map((product) => ({
            id: product.productId,
            changes: product,
          })),
          state.products,
        ),
      };
    },
  ),
  on(actions.loadEpicShowProductsSuccess, (state, { products }): State => {
    const newProducts = epicShowProductsAdapter.upsertMany(
      products,
      state.products,
    );

    return {
      ...state,
      epicShowProducts: newProducts,
    };
  }),
  on(actions.loadOpenedListProductsError, (state): State => {
    return {
      ...state,
      loading: false,
    };
  }),
  on(actions.deleteProductFromOpenedList, (state, action): State => {
    let newTotalAvailable = 0;

    const newProducts = adapter.removeOne(
      action.product.productId,
      state.products,
    );

    if (state.totalAvailable) {
      newTotalAvailable = state.totalAvailable - 1;
    }

    const total = state.total ? state.total - 1 : 0;
    const amount = state.list?.amount ? state.list?.amount - 1 : 0;

    return {
      ...state,
      list: {
        ...(state.list as List),
        amount,
        availableProductsCount: newTotalAvailable,
      },
      products: newProducts,
      total,
      totalAvailable: newTotalAvailable,
    };
  }),
  on(
    actions.deleteProductFromOpenedListSuccess,
    (state, { products }): State => {
      if (products.length > 0) {
        // remove products and recalculate amounts
        const list = state.list as List;

        const newProducts = adapter.removeOne(
          products[0].productId,
          state.products,
        );

        const productsReference = [
          ...(list?.productsReference ?? []).filter(
            (productReference) =>
              !products.some(
                ({ productId, brandId }) =>
                  productId === productReference.productId &&
                  brandId === productReference.brandId,
              ),
          ),
        ];

        // calculate only diff as total is also changed in deleteProductFromOpenedList action
        const deletedCount = Math.max(
          (list?.productsReference ?? []).length - productsReference.length,
          0,
        );

        const totals = calculateTotalProductsOnDelete(
          deletedCount,
          state.size,
          state.total,
          state.totalAvailable,
          list,
        );

        return {
          ...state,
          page: totals.currentPage,
          list: {
            ...list,
            amount: totals.amount,
            availableProductsCount: totals.totalAvailable,
            productsReference,
          },
          products: newProducts,
          total: totals.total,
          totalAvailable: totals.totalAvailable,
        };
      }

      return state;
    },
  ),
  on(actions.addProductToOpenedListSuccess, (state, { products }): State => {
    const { totalProductsPerAvailability, totalAvailable, total, amount } =
      mapAvailableUnavailableTotalProducts(products, state);

    const loadListInDemand = !!totalProductsPerAvailability.available;

    const list = state.list as List;

    const newProducts = products.map((product) => ({
      productId: product.productId,
      brandId: product.brandId,
    })) as ProductReference[];

    return {
      ...state,
      loadListInDemand,
      list: {
        ...list,
        amount,
        availableProductsCount: totalAvailable,
        productsReference: [...(list?.productsReference ?? []), ...newProducts],
      },
      total,
      totalAvailable,
    };
  }),
  on(actions.deleteProductsFromOpenedList, (state, { products }): State => {
    const list = state.list as List;

    if (products.length > 0) {
      const productsReference = [
        ...(list.productsReference ?? []).filter(
          (productReference) =>
            !products.some(
              ({ productId, brandId }) =>
                productId === productReference.productId &&
                brandId === productReference.brandId,
            ),
        ),
      ];

      return {
        ...state,
        page: 0,
        list: {
          ...list,
          productsReference,
        },
      };
    }

    return state;
  }),
  on(actions.resetOpenedListProducts, (state): State => {
    return {
      ...state,
      products: adapter.removeAll(state.products),
      page: 0,
    };
  }),
  on(actions.clearOpenedList, (): State => {
    return initialState;
  }),

  on(
    actions.upsertEpicShowProductsReferences,
    (state, { references }): State => {
      return {
        ...state,
        epicShowProductReferences: epicShowProductReferencesAdapter.setAll(
          references,
          state.epicShowProductReferences,
        ),
      };
    },
  ),

  on(actions.resetProductListStore, () => initialState),
);

/**
 * Reducer export
 * @param state the State
 * @param action the Action emitted
 */
export function reducer(state: State | undefined, action: Action) {
  return productListOpenedReducer(state, action);
}

function addProductsToTop(
  newProducts: EntityState<Product>,
  products: Product[],
  oldProductIds: string[],
) {
  return (newProducts.ids as string[]).sort((a, b) => {
    const aInNewSet = products.some((p) => p.productId === a);
    const aInOldSet = oldProductIds.includes(a);
    const aNewItem = aInNewSet && !aInOldSet;

    const bInNewSet = products.some((p) => p.productId === b);
    const bInOldSet = oldProductIds.includes(b);
    const bNewItem = bInNewSet && !bInOldSet;

    if (aNewItem && !bNewItem) return -1;
    else if (!aNewItem && bNewItem) return 1;
    return 0;
  });
}
